import React, { ReactNode } from 'react';

import { Box } from '@rbilabs/components-library/build/components/layout';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import LoadingAnimation from 'components/loading-animation';
import AuthContainer from 'pages/authentication/auth-container';
import { useLocationContext } from 'state/location';
import { appUriScheme } from 'utils/environment';
import noop from 'utils/noop';

import { messages } from './messages';
import { OpenInAppForm } from './open-in-app-form';
import theme from './theme';

export const Heading = styled.h2`
  color: ${theme.headingColor};
  text-transform: ${Styles.textTransform.headlines};
`;

export const CenteredText = styled.div`
  text-align: center;
  font-size: 0.9375rem;
  color: ${theme.centeredTextColor};
  outline: none;
`;

const ContinueOnWebsiteLinkContainer = styled.div<{ extraPadding?: boolean }>`
  margin: ${p => (p.extraPadding ? '1.5rem auto 0' : 'auto')};
`;

const LoadingContainer = styled.div`
  margin: 2rem 0;
  background-color: ${Styles.color.background};

  ${Styles.desktop`
    background-color: ${Styles.color.white};
  `}
`;

interface IOpenInAppForm {
  continueInWebText: ReactNode;
  onContinueOnWebClick(): Promise<void> | void;
  loading?: boolean;
  subHeaderText?: ReactNode;
}

export const OpenInApp = ({
  onContinueOnWebClick,
  loading,
  subHeaderText = null,
  continueInWebText,
}: IOpenInAppForm) => {
  const { location } = useLocationContext();
  const { formatMessage } = useIntl();
  const params = new URLSearchParams(location.search);
  const appUri = `${appUriScheme()}${location.pathname.substring(1)}`;

  return (
    <AuthContainer>
      <Heading>{formatMessage(messages.authContinueHeader)}</Heading>
      <CenteredText
        aria-label={formatMessage(messages.visuallyHiddenOpenInAppOrBrowser)}
        tabIndex={0}
      >
        {subHeaderText}
        <OpenInAppForm appUri={appUri} urlSearchParams={params}>
          <Box width="100%" center margin="15px 0">
            <ActionButton data-testid="continue-in-app-auth" onClick={noop}>
              {formatMessage(messages.authContinueApp)}
            </ActionButton>
          </Box>
        </OpenInAppForm>
        {loading ? (
          <LoadingContainer>
            <LoadingAnimation fillColor={Styles.color.primary} />
          </LoadingContainer>
        ) : (
          <ContinueOnWebsiteLinkContainer extraPadding={!subHeaderText}>
            <Box width="100%" center>
              <ActionButton
                variant={ActionButtonVariants.TEXT_ONLY}
                data-testid="continue-in-web-auth"
                onClick={onContinueOnWebClick}
              >
                {continueInWebText}
              </ActionButton>
            </Box>
          </ContinueOnWebsiteLinkContainer>
        )}
      </CenteredText>
    </AuthContainer>
  );
};
